export const allTrue = (...arr: boolean[]): boolean => arr.every((item) => item === true);

export const anyTrue = (...arr: boolean[]): boolean => arr.some((item) => item === true);

export function isEmptyObject<T>(item: T): boolean {
  return Object.keys(item).length === 0 && item.constructor === Object;
}

/**
 * Type guard to check if a value is defined (not null and not undefined)
 * @param value - The value to check
 * @returns true if the value is neither null nor undefined
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Check if a value is empty (null, undefined, or empty string)
 * Useful for form input validation
 * @param value - The value to check
 * @returns true if the value is null, undefined, or empty string
 */
export function isEmptyValue(value: unknown): value is null | undefined | "" {
  return value === "" || value === null || value === undefined;
}

/**
 * Type guard to check if a value is a string
 * @param value - The value to check
 * @returns true if the value is a string
 */
export function isString(value: unknown): value is string {
  return typeof value === "string";
}

/**
 * Type guard to check if a value is a number (and not NaN)
 * @param value - The value to check
 * @returns true if the value is a number and not NaN
 */
export function isNumber(value: unknown): value is number {
  return typeof value === "number" && !isNaN(value);
}

/**
 * Type guard to check if a value is an object (not null, not array)
 * @param value - The value to check
 * @returns true if the value is a plain object
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Safely parse a value to a number
 * Returns the parsed number if valid, otherwise returns the fallback value
 * @param value - The value to parse (can be string, number, or unknown)
 * @param fallback - The fallback value if parsing fails (default: 0)
 * @returns The parsed number or fallback value
 */
export function parseNumber(value: unknown, fallback: number = 0): number {
  if (isNumber(value)) {
    return value;
  }

  if (isString(value)) {
    const parsed = parseFloat(value);
    return isNumber(parsed) ? parsed : fallback;
  }

  return fallback;
}

/**
 * Safely retrieves a value from a record-like object, returning a fallback if
 * the source is not a valid record. Curried for partial application.
 * @param fallback - The default value when key is missing or record is invalid
 * @returns A function that takes (record, key) and returns the value or fallback
 */
export const getFromRecord =
  <T>(fallback: T) =>
  (record: unknown, key: string): T =>
    isObject(record) ? (record as Record<string, T>)[key] ?? fallback : fallback;
